import { Col, Form, Input, InputNumber, Row, UploadFile, Upload } from 'antd';
import { FC, useState } from 'react';
import Buttons from 'components/Global/Buttons';
import { formatToDateOnly, invalidText, toastText } from 'utils/utils';
import './index.scss';
import styles from './index.module.scss';
import { SideDrawerBodyProps } from './types';
import DatePickerField from 'components/Global/DatePicker';
import dayjs from 'dayjs';
import Dragger from 'antd/es/upload/Dragger';
import { InboxOutlined } from '@ant-design/icons';

import { workersCompensationApi } from 'Api/workersCompensation';
import { UploadChangeParam } from 'antd/es/upload';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCompanyWizard } from '../../../../../../redux/actions/payrollSettingWizardAction';
import { AppDispatch } from 'redux/store';

const AddWorkerSideDrawer: FC<SideDrawerBodyProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [date, setDate] = useState<any>(dayjs());
	const [file, setFile] = useState<any>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);

	const [compensationRate, setCompensationRate] = useState<number | null>(
		null
	);
	const [compensationLimit, setCompensationLimit] = useState<number | null>(
		null
	);

	const dispatch = useDispatch<AppDispatch>();

	const { baseCurrencySymbol } = useSelector(
		(state: any) => state.userProfile.data
	);

	let isRemoving = false;

	const [form] = Form.useForm();

	const {
		closeDrawerByAnimation,
		fetchLatestCompensationData,
		fetchAllCompensationDetails,
	} = props;

	const handleDate = (value: any) => {
		setDate(value);
	};

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: loading,
			onclick: () => {
				closeDrawerByAnimation();
			},
		},
		{
			text: 'Save',
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			onclick: () => {},
		},
	];

	const propsUpload = {
		name: 'file',
		// accept: '.xls,.xlsx,.csv',
		maxCount: 1,
		fileList: fileList,
		beforeUpload: (file: UploadFile) => {
			if (!file) {
				return;
			}

			const isLt1M = file.size! / 1024 / 1024 < 5;
			if (!isLt1M) {
				toastText('File must be smaller than 5MB!', 'error');
				return Upload.LIST_IGNORE;
			}

			setFile(file);
			setFileList([file]);
			return false; // Prevents automatic upload
		},
		onChange(info: UploadChangeParam<UploadFile>) {
			const { file } = info;
			if (!isRemoving) {
				setFile(file);
				setFileList([file]);
			} else {
				isRemoving = false;
				setFile(null);
				setFileList([]);
			}
		},
		onDrop: () => {
			setFile(null);
			setFileList([]);
		},
		onRemove: () => {
			isRemoving = true;
		},
	};

	const handleSubmit = async (values: any) => {
		setLoading(true);
		const compensationFormData = new FormData();
		compensationFormData.append(
			'effectiveDate',
			formatToDateOnly(values.effectiveDate)
		);
		compensationFormData.append(
			'compensationRate',
			values.compensationRate
		);
		compensationFormData.append(
			'compensationLimit',
			values.compensationLimit
		);
		compensationFormData.append(
			'notes',
			invalidText(values.notes) ? null : values.notes
		);
		compensationFormData.append('moduleName', 'WORKERS_COMPENSATION');
		compensationFormData.append('file', file as Blob);

		try {
			const response = await workersCompensationApi.createCompensation(
				compensationFormData
			);

			let message =
				response?.data?.message ||
				'Workers comp details saved successfully';
			toastText(message, 'success');
			dispatch(fetchCompanyWizard());
			closeDrawerByAnimation(true);
			fetchLatestCompensationData();
			fetchAllCompensationDetails();
		} catch (error: any) {
			let message = 'Failed to save compensation details';

			if (error.response.data.message) {
				message = error.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setLoading(false);
		}
	};

	const validatePercentage = (_: any, value: number) => {
		if (value < 0 || value > 100) {
			return Promise.reject(new Error('Percentage must be between 0 and 100'));
		}
		return Promise.resolve();
	};

	return (
			<div className={styles['side-drawer-body']}>
				<Form
					name="basic"
					className={styles['side-drawer-form']}
					form={form}
					labelCol={{ span: 8 }}
					wrapperCol={{ span: 24 }}
					style={{ maxWidth: 600 }}
					onFinish={handleSubmit}
					initialValues={{ effectiveDate: date }}
					autoComplete="off"
				>
					<p className={styles['form-container-head-warning']}>
						<b>
							{' '}
							<sup>*</sup>
						</b>{' '}
						Indicated mandatory fields
					</p>
					<div className={styles['side-drawer-form__inputs']}>
						<label
							className={styles['side-drawer-form__napsa--label']}
						>
							{'Effective Date'}{' '}
							<span className="required-color">*</span>
						</label>
						<div>
							<Form.Item
								name="effectiveDate"
								rules={[
									{
										required: true,
										message: 'Please select a date',
									},
								]}
							>
								<DatePickerField
									onChange={handleDate}
									value={date}
									isError={false}
									disabled={false}
									placeholder="Select Date"
									required
									name="effectiveDate"
									// disabledBeforeDates={dayjs().startOf('day')}
								/>
							</Form.Item>
						</div>
					</div>

					<Row>
						<Col span={12} className="input-column">
							<div className={styles['side-drawer-form__inputs']}>
								<label
									className={
										styles['side-drawer-form__napsa--label']
									}
								>
									{'Compensation Rate'}{' '}
									<span className="required-color">*</span>
								</label>

								<Form.Item
									name="compensationRate"
									rules={[
										{
											required: true,
											message:
												'Please enter compensation rate',
										},
										{ validator: validatePercentage },
									]}
								>
									<div>
										<InputNumber
											value={compensationRate}
											addonAfter="%"
											onChange={setCompensationRate}
											min={0}
											size="large"
											max={100}
											step={0.01}
											parser={(value: any) => {
												const parsedValue = parseFloat(
													value?.replace('%', '') ||
														'0'
												);
												return isNaN(parsedValue)
													? 0
													: parsedValue;
											}}
											style={{ width: '100%' }}
										/>
										<span className={styles['helper-text']}>
											Percentage of total compensation
											rate
										</span>
									</div>
								</Form.Item>
							</div>
						</Col>

						<Col span={12} className="input-column">
							<div className={styles['side-drawer-form__inputs']}>
								<label
									className={
										styles['side-drawer-form__napsa--label']
									}
								>
									{'Compensation Limit'}{' '}
									<span className="required-color">*</span>
								</label>
								<Form.Item
									name="compensationLimit"
									rules={[
										{
											required: true,
											message:
												'Please enter compensation limit',
										},
									]}
								>
									<div>
										<InputNumber
											value={compensationLimit}
											onChange={setCompensationLimit}
											addonAfter={baseCurrencySymbol}
											size="large"
											// step={0.01}
											parser={(value: any) => {
												const parsedValue = parseFloat(
													value || '0'
												);
												return isNaN(parsedValue)
													? 0
													: parsedValue;
											}}
											style={{ width: '100%' }}
										/>
										<span className={styles['helper-text']}>
											Compensation Limit for USD
										</span>
									</div>
								</Form.Item>
							</div>
						</Col>
					</Row>

					<div className={styles['side-drawer-form__inputs']}>
						<label
							className={styles['side-drawer-form__napsa--label']}
						>
							{'Attachment'}
						</label>
						<Form.Item name="attachment" required>
							<Dragger {...propsUpload} className="test">
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-hint">
									Drag and drop a file or choose file from
									{/* Device Supported file formats. */}
									<br />
									{/* XLS, XLSX, CSV |{' '} */}
									<span className="color-purple">
										Maximum file size: 5MB
									</span>
								</p>
							</Dragger>
						</Form.Item>
					</div>

					<div className={styles['side-drawer-form__inputs']}>
						<label
							className={styles['side-drawer-form__napsa--label']}
						>
							{'Notes'}
						</label>
						<Form.Item name="notes" wrapperCol={{ span: 24 }}>
							<Input.TextArea
								className={styles['side-drawer-form--textarea']}
							/>
						</Form.Item>
					</div>

					<div className={styles['side-drawer-form__buttons']}>
						<Form.Item>
							<Buttons buttons={buttons} />
						</Form.Item>
					</div>
				</Form>
			</div>
	);
};

export default AddWorkerSideDrawer;
